import React from 'react';
import { Box } from '@mui/material';

import { PropertySelectField } from '@paragon/client-tools';
import { BraceInstallationLocation, LineLocation, Member } from '@paragon/contract-library';
import { hasTrussDesignPermission as hasTrussDesignPermissionSelector } from '@paragon/login-tools';
import { findAndCalculateLine, getDirectionalLineLocationTerms } from '@paragon/relative-geometry';

import {
  setBraceInstallationLocation,
  setBraceSide,
  setMemberLineLocation,
} from '../../../../../store/actions/editMemberActions';
import { getActiveComponentDesign } from '../../../../../store/selectors/getActiveComponentDesignSelector';
import { getReferenceLinesForActiveComponent } from '../../../../../store/selectors/getReferenceLinesForActiveComponent';
import { applicationOnPickingMemberLine } from '../../../../../store/slices/applicationSlice';
import { ReferenceLinePicker } from '../../../../controls/ReferenceLinePicker';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { BracedMember } from '../BracedMember';
import { LineLocationSelect } from '../LineLocationSelect';
import { PropertiesPanelSubtitle } from '../PropertiesPanelSubtitle';

interface Props {
  member: Member;

  disabled?: boolean;
  showAdvanced: boolean;
}

const BRACE_LOCATIONS: readonly BraceInstallationLocation[] = ['Front', 'Center', 'Back'];

export function MemberGeometrySubPanel({ member, disabled, showAdvanced }: Props) {
  const dispatch = useAppDispatch();

  const componentDesign = useAppSelector(getActiveComponentDesign);
  const referenceLines = useAppSelector(getReferenceLinesForActiveComponent);

  const hasTrussDesignPermission = useAppSelector(hasTrussDesignPermissionSelector);

  if (!hasTrussDesignPermission || member.relativeGeometry == null || referenceLines == null) return null;

  const isBrace = member.relativeGeometry?.isBrace ?? false;

  const validBraceLocations =
    member.relativeGeometry?.isBrace && member.relativeGeometry.side === 'Center'
      ? BRACE_LOCATIONS.filter((location) => location !== 'Center')
      : BRACE_LOCATIONS;

  return (
    <Box sx={{ mt: 1 }}>
      <PropertiesPanelSubtitle>Geometry</PropertiesPanelSubtitle>

      <Box sx={{ mt: 0.5 }}>
        {!isBrace && (
          <Box sx={{ display: 'flex' }}>
            <LineLocationSelect
              relativeGeometry={member.relativeGeometry}
              submit={async (lineLocation) => {
                await dispatch(setMemberLineLocation(componentDesign!.guid, member, lineLocation));
              }}
              disabled={disabled ?? false}
            />
          </Box>
        )}

        {member.relativeGeometry.isBrace && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <PropertySelectField<LineLocation>
                sx={{ m: 1 }}
                property="Side"
                value={member.relativeGeometry.side}
                options={
                  member.relativeGeometry.braceInstallationLocation === 'Center'
                    ? ['Starboard', 'Port']
                    : ['Starboard', 'Center', 'Port']
                }
                getName={(side) => {
                  const flippedSide = side === 'Port' ? 'Starboard' : side === 'Starboard' ? 'Port' : 'Center';
                  const directionalTerm =
                    flippedSide === 'Center'
                      ? 'On'
                      : getDirectionalLineLocationTerms(
                          findAndCalculateLine(member.relativeGeometry?.lineGuid ?? null, componentDesign!.component)
                            ?.direction ?? null,
                        ).terms[flippedSide];
                  return `${directionalTerm} Braced Member`;
                }}
                submit={(value) => {
                  dispatch(setBraceSide(componentDesign!.guid, member, value));
                }}
                disabled={disabled}
              />
              <PropertySelectField
                sx={{ m: 1 }}
                property="Placement"
                value={member.relativeGeometry.braceInstallationLocation}
                options={validBraceLocations}
                submit={(value) => {
                  dispatch(setBraceInstallationLocation(componentDesign!.guid, member, value));
                }}
                disabled={disabled}
              />
            </Box>
            <BracedMember bracedMemberGuid={member.relativeGeometry.bracedMemberGuid} />
          </>
        )}

        {!isBrace && showAdvanced && (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <ReferenceLinePicker
              elevation={1}
              lineGuid={member.relativeGeometry.lineGuid}
              startSelectLine={() => {
                dispatch(applicationOnPickingMemberLine(member.guid));
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
